import dayjs from 'dayjs'

/**
 * Format currency values
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: 'ZAR')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'ZAR') => {
  if (amount === null || amount === undefined) return 'R 0.00'

  const formatter = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  return formatter.format(amount).replace('ZAR', 'R')
}

/**
 * Format percentage values
 * @param {number} value - The percentage value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return '0%'
  return `${value.toFixed(decimals)}%`
}

/**
 * Get color based on status
 * @param {string} status - The status value
 * @returns {string} Color value
 */
export const getStatusColor = (status) => {
  const statusColors = {
    awaiting_payment_patient: '#fa8c16',
    awaiting_payment_aid: '#1890ff',
    handed_over: '#f5222d',
    payment_plan: '#722ed1',
    disputed: '#fa541c',
    completed: '#52c41a',
    coid: '#13c2c2',
    fem: '#eb2f96',
    saps: '#1890ff',
    rma: '#a0d911',
    pending: '#faad14',
    approved: '#52c41a',
    rejected: '#f5222d',
    under_review: '#722ed1',
    paid: '#52c41a',
    outstanding: '#fa8c16',
    overdue: '#f5222d',
    refunded: '#13c2c2',
    payment: '#52c41a',
    medical_aid_payment: '#52c41a',
    invoice: '#1890ff',
    claim: '#722ed1',
    credit: '#13c2c2',
    debit: '#fa8c16',
    adjustment: '#722ed1',
    refund: '#13c2c2',
    write_off: '#f5222d',
    credit_note: '#13c2c2',
    interest: '#fa8c16',
    fee: '#fa8c16',
    payment_reversal: '#f5222d',
    transfer: '#1890ff',
    error: '#f5222d',
    warning: '#faad14',
    info: '#1890ff',
    success: '#52c41a'
  }

  return statusColors[status] || '#8c8c8c'
}

/**
 * Get color based on aging bucket
 * @param {string} aging - The aging bucket value
 * @returns {string} Color value
 */
export const getAgingColor = (aging) => {
  const agingColors = {
    '0-29': '#52c41a',
    '30-59': '#faad14',
    '60-89': '#fa8c16',
    '90+': '#f5222d'
  }

  return agingColors[aging] || '#8c8c8c'
}

/**
 * Get priority color
 * @param {string} priority - The priority level
 * @returns {string} Color value
 */
export const getPriorityColor = (priority) => {
  const priorityColors = {
    high: '#f5222d',
    medium: '#faad14',
    low: '#52c41a',
    urgent: '#ff4d4f',
    normal: '#1890ff'
  }

  return priorityColors[priority] || '#8c8c8c'
}

/**
 * Calculate aging bucket based on date
 * @param {string|Date} date - The date to calculate aging from
 * @returns {string} Aging bucket
 */
export const calculateAgingBucket = (date) => {
  if (!date) return '0-29'

  const daysDiff = dayjs().diff(dayjs(date), 'days')

  if (daysDiff <= 29) return '0-29'
  if (daysDiff <= 59) return '30-59'
  if (daysDiff <= 89) return '60-89'
  return '90+'
}

/**
 * Calculate days outstanding
 * @param {string|Date} date - The date to calculate from
 * @returns {number} Days outstanding
 */
export const calculateDaysOutstanding = (date) => {
  if (!date) return 0
  return dayjs().diff(dayjs(date), 'days')
}

/**
 * Format date for display
 * @param {string|Date} date - The date to format
 * @param {string} format - Date format (default: 'DD MMM YYYY')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'DD MMM YYYY') => {
  if (!date) return 'N/A'
  return dayjs(date).format(format)
}

/**
 * Format date and time for display
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  if (!date) return 'N/A'
  return dayjs(date).format('DD MMM YYYY HH:mm')
}

/**
 * Get relative time (e.g., "2 days ago")
 * @param {string|Date} date - The date to calculate from
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date) => {
  if (!date) return 'Never'
  return dayjs(date).fromNow()
}

/**
 * Calculate collection rate
 * @param {number} collected - Amount collected
 * @param {number} total - Total amount
 * @returns {number} Collection rate percentage
 */
export const calculateCollectionRate = (collected, total) => {
  if (!total || total === 0) return 0
  return ((collected / total) * 100).toFixed(1)
}

/**
 * Generate account number
 * @param {string} prefix - Prefix for account number (default: 'A')
 * @returns {string} Generated account number
 */
export const generateAccountNumber = (prefix = 'A') => {
  const timestamp = Date.now().toString().slice(-6)
  const random = Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, '0')
  return `${prefix}${timestamp}${random}`
}

/**
 * Generate file number
 * @param {string} type - Type of file (default: 'MED')
 * @param {string} year - Year (default: current year)
 * @returns {string} Generated file number
 */
export const generateFileNumber = (type = 'MED', year = dayjs().format('YYYY')) => {
  const timestamp = Date.now().toString().slice(-6)
  return `${type}-${year}-${timestamp}`
}

/**
 * Validate South African ID number
 * @param {string} idNumber - ID number to validate
 * @returns {boolean} Whether ID is valid
 */
export const validateSAIdNumber = (idNumber) => {
  if (!idNumber || idNumber.length !== 13) return false

  // Basic validation - check if all characters are digits
  if (!/^\d+$/.test(idNumber)) return false

  // Luhn algorithm check
  const digits = idNumber.split('').map(Number)
  let sum = 0

  for (let i = 0; i < 12; i++) {
    if (i % 2 === 0) {
      sum += digits[i]
    } else {
      const doubled = digits[i] * 2
      sum += doubled > 9 ? doubled - 9 : doubled
    }
  }

  const checkDigit = (10 - (sum % 10)) % 10
  return checkDigit === digits[12]
}

/**
 * Validate email address
 * @param {string} email - Email address to validate
 * @returns {boolean} Whether email is valid
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate phone number (SA format)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} Whether phone is valid
 */
export const validatePhoneNumber = (phone) => {
  const phoneRegex = /^(\+27|0)[0-9]{9}$/
  return phoneRegex.test(phone.replace(/\s+/g, ''))
}

/**
 * Format phone number for display
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return ''

  const cleaned = phone.replace(/\D/g, '')

  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`
  }

  if (cleaned.length === 11 && cleaned.startsWith('27')) {
    return `+27 ${cleaned.slice(2, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`
  }

  return phone
}

/**
 * Calculate total from array of objects
 * @param {Array} items - Array of objects
 * @param {string} field - Field to sum
 * @returns {number} Total value
 */
export const calculateTotal = (items, field) => {
  if (!items || !Array.isArray(items)) return 0
  return items.reduce((sum, item) => sum + (item[field] || 0), 0)
}

/**
 * Group items by field
 * @param {Array} items - Array of objects
 * @param {string} field - Field to group by
 * @returns {Object} Grouped items
 */
export const groupBy = (items, field) => {
  if (!items || !Array.isArray(items)) return {}

  return items.reduce((groups, item) => {
    const key = item[field] || 'Unknown'
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(item)
    return groups
  }, {})
}

/**
 * Sort items by field
 * @param {Array} items - Array of objects
 * @param {string} field - Field to sort by
 * @param {string} order - Sort order ('asc' or 'desc')
 * @returns {Array} Sorted items
 */
export const sortBy = (items, field, order = 'asc') => {
  if (!items || !Array.isArray(items)) return []

  return [...items].sort((a, b) => {
    const aVal = a[field] || 0
    const bVal = b[field] || 0

    if (order === 'desc') {
      return bVal > aVal ? 1 : -1
    }
    return aVal > bVal ? 1 : -1
  })
}

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, delay) => {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(null, args), delay)
  }
}

/**
 * Throttle function
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle
  return (...args) => {
    if (!inThrottle) {
      func.apply(null, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

/**
 * Deep clone object
 * @param {Object} obj - Object to clone
 * @returns {Object} Cloned object
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj.getTime())
  if (obj instanceof Array) return obj.map((item) => deepClone(item))
  if (typeof obj === 'object') {
    const cloned = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = deepClone(obj[key])
      }
    }
    return cloned
  }
}

/**
 * Check if object is empty
 * @param {Object} obj - Object to check
 * @returns {boolean} Whether object is empty
 */
export const isEmpty = (obj) => {
  if (obj === null || obj === undefined) return true
  if (Array.isArray(obj)) return obj.length === 0
  if (typeof obj === 'object') return Object.keys(obj).length === 0
  if (typeof obj === 'string') return obj.trim().length === 0
  return false
}

/**
 * Generate random color
 * @returns {string} Random hex color
 */
export const generateRandomColor = () => {
  const colors = [
    '#1890ff',
    '#52c41a',
    '#faad14',
    '#f5222d',
    '#722ed1',
    '#13c2c2',
    '#eb2f96',
    '#fa8c16',
    '#a0d911',
    '#2f54eb'
  ]
  return colors[Math.floor(Math.random() * colors.length)]
}

/**
 * Export data as CSV
 * @param {Array} data - Data to export
 * @param {string} filename - Filename for export
 */
export const exportToCSV = (data, filename = 'export.csv') => {
  if (!data || data.length === 0) return

  const headers = Object.keys(data[0])
  const csvContent = [
    headers.join(','),
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header] || ''
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value
        })
        .join(',')
    )
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Get transaction type display info
 * @param {string} type - Transaction type
 * @returns {Object} Display info with label and color
 */
export const getTransactionTypeInfo = (type) => {
  const typeInfo = {
    invoice: { label: 'Invoice', color: '#1890ff', icon: 'file-invoice' },
    payment: { label: 'Payment', color: '#52c41a', icon: 'dollar-sign' },
    medical_aid_payment: { label: 'Medical Aid Payment', color: '#52c41a', icon: 'shield-alt' },
    medical_aid_allocation: { label: 'Medical Aid Allocated', color: '#722ed1', icon: 'hand-holding-medical' },
    medical_aid_approval: { label: 'Medical Aid Approved', color: '#13c2c2', icon: 'check-circle' },
    credit: { label: 'Credit', color: '#52c41a', icon: 'plus-circle' },
    debit: { label: 'Debit', color: '#fa8c16', icon: 'minus-circle' },
    adjustment: { label: 'Adjustment', color: '#722ed1', icon: 'balance-scale' },
    refund: { label: 'Refund', color: '#13c2c2', icon: 'undo' },
    write_off: { label: 'Write Off', color: '#f5222d', icon: 'times-circle' },
    credit_note: { label: 'Credit Note', color: '#13c2c2', icon: 'file-alt' },
    interest: { label: 'Interest', color: '#fa8c16', icon: 'percentage' },
    fee: { label: 'Fee', color: '#fa8c16', icon: 'tag' },
    payment_reversal: { label: 'Payment Reversal', color: '#f5222d', icon: 'history' },
    transfer: { label: 'Transfer', color: '#1890ff', icon: 'exchange-alt' }
  }

  return typeInfo[type] || { label: type, color: '#8c8c8c', icon: 'question-circle' }
}

/**
 * Validate EFT transaction has a reference number
 * @param {string} paymentMethod - Payment method
 * @param {string} reference - Reference number
 * @throws {Error} If EFT payment is missing reference
 */
export const validateEftReference = (paymentMethod, reference) => {
  const BillingEnums = require('./enums').default
  if (paymentMethod === BillingEnums.paymentMethods.EFT && (!reference || reference.trim().length === 0)) {
    throw new Error('EFT payments must include a reference number')
  }
}

/**
 * Get validation rules for payment reference field
 * @returns {Array} Ant Design form validation rules
 */
export const getPaymentReferenceRules = () => [
  ({ getFieldValue }) => ({
    validator(_, value) {
      const paymentMethod = getFieldValue('paymentMethod')
      if (paymentMethod === 'eft' && (!value || value.trim().length === 0)) {
        return Promise.reject(new Error('Reference number is required for EFT payments'))
      }
      return Promise.resolve()
    }
  })
]

/**
 * Validate payment amount against outstanding balances
 * @param {number} paymentAmount - Payment amount
 * @param {string} paymentType - 'patient' or 'medical_aid'
 * @param {number} patientBalance - Outstanding patient balance
 * @param {number} medicalAidBalance - Outstanding medical aid balance
 * @returns {Object} Validation result with warnings/errors
 */
export const validatePaymentBalance = (paymentAmount, paymentType, patientBalance, medicalAidBalance) => {
  const amount = parseFloat(paymentAmount) || 0
  const patientBal = parseFloat(patientBalance) || 0
  const medicalAidBal = parseFloat(medicalAidBalance) || 0

  const result = {
    isValid: true,
    warnings: [],
    errors: [],
    allowOverride: false
  }

  if (paymentType === 'patient' || !paymentType) {
    if (amount > patientBal) {
      if (medicalAidBal > 0) {
        result.errors.push({
          title: 'Payment Exceeds Patient Balance',
          message: `Payment of R${amount.toFixed(2)} exceeds patient balance of R${patientBal.toFixed(
            2
          )}. Medical aid balance of R${medicalAidBal.toFixed(2)} is still outstanding.`,
          maxAllowed: patientBal,
          suggestion: `Maximum patient payment: R${patientBal.toFixed(2)}`
        })
        result.isValid = false
        result.allowOverride = true
      } else {
        result.warnings.push({
          title: 'Overpayment Will Create Credit',
          message: `Payment of R${amount.toFixed(2)} exceeds patient balance of R${patientBal.toFixed(
            2
          )}. This will create an account credit of R${(amount - patientBal).toFixed(2)}.`
        })
      }
    }
  }

  if (paymentType === 'medical_aid') {
    if (amount > medicalAidBal) {
      if (patientBal > 0) {
        result.errors.push({
          title: 'Payment Exceeds Medical Aid Balance',
          message: `Payment of R${amount.toFixed(2)} exceeds medical aid balance of R${medicalAidBal.toFixed(
            2
          )}. Patient balance of R${patientBal.toFixed(2)} is still outstanding.`,
          maxAllowed: medicalAidBal,
          suggestion: `Maximum medical aid payment: R${medicalAidBal.toFixed(2)}`
        })
        result.isValid = false
        result.allowOverride = true
      } else {
        result.warnings.push({
          title: 'Overpayment Will Create Credit',
          message: `Payment of R${amount.toFixed(2)} exceeds medical aid balance of R${medicalAidBal.toFixed(
            2
          )}. This will create an account credit of R${(amount - medicalAidBal).toFixed(2)}.`
        })
      }
    }
  }

  return result
}
